import {
  SubscriptionDTO,
  CreateOrUpdatePolicyRequest,
} from "@shared/types/common";
import { jmisService } from "@shared/services/jmis.ts";
import { useNotifications } from "@shared/module-transformers/useNotifications.ts";
import { messagesService } from "@shared/services/messages.ts";
import { useDnsRoutingStore } from "@shared/stores/dnsRouting.ts";
import { useSubscriptionsStore } from "@shared/stores/subscriptions.ts";
import useSettingsHelpers from "@shared/use/useSettingsHelpers.ts";
import { permissions } from "@shared/services/permissions.ts";
import logger from "@shared/utils/logger.ts";

// Module scope on purpose: every useDnsRoutingHelper() call builds its own
// closures, so a per-instance counter could not order the page's initial fetch
// against the polling component's.
let latestReadinessRequestId = 0;

export const useDnsRoutingHelper = () => {
  const dnsRoutingStore = useDnsRoutingStore();
  const subscriptionsStore = useSubscriptionsStore();
  const fetchRoutingPolicies = async (currentSubscription: SubscriptionDTO) => {
    const {
      isEntitledDnsRoutingManualFailoverPolicy,
      isEntitledDnsRoutingGeoLocationPolicy,
    } = useSettingsHelpers();
    const isEntitledDns =
      isEntitledDnsRoutingGeoLocationPolicy.value ||
      isEntitledDnsRoutingManualFailoverPolicy.value;
    const hasDnsAccessPermissions = permissions.has("cloudOps:read");
    if (!isEntitledDns || !hasDnsAccessPermissions) {
      return;
    }
    try {
      const response = await jmisService.getRoutingPolicies(
        currentSubscription.accountNumber,
      );
      dnsRoutingStore.setRoutingPoliciesResponse(response);
    } catch (e) {
      logger.error(messagesService.dns_routing_fetch_policies_failed, e);
      useNotifications().error(
        messagesService.dns_routing_fetch_policies_failed,
      );
    }
  };

  const fetchJpdHealthStatus = async (currentSubscription: SubscriptionDTO) => {
    // Subscription switches and overlapping polls leave earlier requests in
    // flight. Only the newest one, for the account still on screen, may land:
    // the id alone would let a request outlive its subscription, the account
    // alone would let an A -> B -> A round trip revive a superseded request.
    const requestId = ++latestReadinessRequestId;
    const isRequestStillRelevant = () =>
      requestId === latestReadinessRequestId &&
      subscriptionsStore.currentSubscription?.accountNumber ===
        currentSubscription.accountNumber;
    try {
      const technicalServerNames = currentSubscription.jpus.map(
        (jpd) => jpd.technicalServerName,
      );
      const readinessResponse = await jmisService.getJpdsHealthStatus({
        technical_server_names: technicalServerNames,
      });
      if (!isRequestStillRelevant()) {
        return;
      }
      dnsRoutingStore.setJpdsReadinessResponse(readinessResponse);
    } catch (e) {
      logger.error(messagesService.dns_routing_fetch_jpd_readiness_failed, e);
      if (isRequestStillRelevant()) {
        useNotifications().error(
          messagesService.dns_routing_fetch_jpd_readiness_failed,
        );
      }
    } finally {
      if (isRequestStillRelevant()) {
        dnsRoutingStore.setHasLoadedJpdsReadiness(true);
      }
    }
  };

  const fetchGeoLocationsList = async () => {
    try {
      const getGeoLocationsResponse = await jmisService.getGeoLocation();
      dnsRoutingStore.setGeoLocationListResponse(getGeoLocationsResponse);
    } catch (e) {
      logger.error(messagesService.dns_routing_fetch_geo_locations_failed, e);
      useNotifications().error(
        messagesService.dns_routing_fetch_geo_locations_failed,
      );
    }
  };

  const updateExistingPolicy = async (
    routingPolicyId: string,
    request: CreateOrUpdatePolicyRequest,
  ) => {
    const routingPolicyModel = await jmisService.updateDnsRoutingPolicy(
      routingPolicyId,
      request,
    );
    return routingPolicyModel;
  };

  return {
    fetchGeoLocationsList,
    fetchRoutingPolicies,
    fetchJpdHealthStatus,
    updateExistingPolicy,
  };
};
